import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMY } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Prohibit, ArrowClockwise, Trash, ShieldCheck, PencilSimple, Check, X, Coin, Plus } from "@phosphor-icons/react";

export default function Users() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "staff" });
  const [saving, setSaving] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const [grantOpen, setGrantOpen] = useState(null); // user object or null
  const [grantAmount, setGrantAmount] = useState("");
  const [granting, setGranting] = useState(false);

  const load = () => api.get("/users").then(r => setRows(r.data)).catch(e =>
    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"));

  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email.trim()) return toast.error("Email required");
    setSaving(true);
    try {
      const { data } = await api.post("/users/invite", form);
      setTempPw({ email: form.email, password: data.temp_password });
      setForm({ email: "", name: "", role: "staff" });
      toast.success("Invite email sent");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const toggleDisabled = async (u) => {
    const action = u.disabled ? "enable" : "disable";
    await api.put(`/users/${u.id}/${action}`);
    toast.success(u.disabled ? "Account enabled" : "Account disabled");
    load();
  };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      await api.put(`/users/${u.id}/role`, { role });
      toast.success(`Role changed to ${role}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const startEditName = (u) => { setEditingId(u.id); setEditingName(u.name || ""); };
  const cancelEditName = () => { setEditingId(null); setEditingName(""); };
  const saveEditName = async (u) => {
    const trimmed = editingName.trim();
    if (!trimmed) return toast.error("Name cannot be empty");
    if (trimmed === (u.name || "")) return cancelEditName();
    try {
      await api.put(`/users/${u.id}/name`, { name: trimmed });
      toast.success("Name updated");
      cancelEditName();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const grantCredits = async () => {
    const n = parseInt(grantAmount, 10);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Enter a positive number of credits");
    setGranting(true);
    try {
      await api.post("/settings/access-tokens/grant-by-email",
                     { email: grantOpen.email, credits: n });
      toast.success(`Granted ${n} credit${n > 1 ? "s" : ""} to ${grantOpen.email}`);
      setGrantOpen(null); setGrantAmount("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setGranting(false); }
  };

  const isAdmin = me?.role === "admin";

  return (
    <div className="p-10 max-w-[1400px]">
      <header className="mb-8 flex items-end justify-between">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Access</div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight">Users</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            Invite colleagues, assign admin or staff roles, disable accounts. Only admins can delete
            clients, delete reminders and manage users.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE] h-10" data-testid="invite-user-btn">
              <UserPlus size={16} weight="bold" className="mr-2" /> Invite User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md rounded-sm">
            <DialogHeader><DialogTitle>Invite User</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input className="mt-1 rounded-sm" value={form.email}
                       onChange={e => setForm({ ...form, email: e.target.value })}
                       placeholder="colleague@firm.my" data-testid="invite-email-input" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Name</Label>
                <Input className="mt-1 rounded-sm" value={form.name}
                       onChange={e => setForm({ ...form, name: e.target.value })}
                       placeholder="Their name" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Role</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[11px] text-muted-foreground">
                A temporary password will be emailed and also shown to you after sending.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={invite} disabled={saving}
                      data-testid="send-invite-btn">
                {saving ? "Sending…" : "Send invite"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      {tempPw && (
        <div className="mb-6 border border-[#F1C21B] bg-[#F1C21B]/10 p-4">
          <div className="font-mono text-xs uppercase tracking-widest text-[#8A6A00] mb-1">Temporary password issued</div>
          <div className="text-sm">Share this with <b>{tempPw.email}</b> if the email is delayed:</div>
          <div className="mt-2 font-mono text-lg tracking-wider">{tempPw.password}</div>
          <button className="mt-2 text-xs underline" onClick={() => setTempPw(null)}>Dismiss</button>
        </div>
      )}

      <div className="border border-border">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="text-left border-b border-border">
              <Th>Email</Th><Th>Name</Th><Th>Role</Th><Th>Credits</Th><Th>Status</Th><Th>Created</Th><Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-6 py-8 text-sm text-muted-foreground">No users yet.</td></tr>}
            {rows.map(u => (
              <tr key={u.id} className="data-row border-b border-border">
                <td className="px-6 py-3">
                  <div>{u.email}</div>
                  {u.id === me?.id && <div className="text-[10px] font-mono uppercase text-[#0F62FE]">You</div>}
                </td>
                <td className="px-6 py-3">
                  {editingId === u.id ? (
                    <div className="flex items-center gap-1">
                      <Input value={editingName}
                             onChange={(e) => setEditingName(e.target.value)}
                             onKeyDown={(e) => {
                               if (e.key === "Enter") saveEditName(u);
                               if (e.key === "Escape") cancelEditName();
                             }}
                             autoFocus
                             className="h-8 rounded-sm text-sm"
                             data-testid={`name-input-${u.id}`} />
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-[#24A148]"
                              onClick={() => saveEditName(u)} title="Save"
                              data-testid={`name-save-${u.id}`}>
                        <Check size={14} weight="bold" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground"
                              onClick={cancelEditName} title="Cancel"
                              data-testid={`name-cancel-${u.id}`}>
                        <X size={14} weight="bold" />
                      </Button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => startEditName(u)}
                            className="group inline-flex items-center gap-2 text-left hover:text-[#0F62FE]"
                            data-testid={`name-edit-${u.id}`} title="Click to edit name">
                      <span>{u.name || "—"}</span>
                      <PencilSimple size={12} weight="bold"
                                    className="opacity-0 group-hover:opacity-70 transition-opacity" />
                    </button>
                  )}
                </td>
                <td className="px-6 py-3">
                  <Select value={u.role} onValueChange={v => changeRole(u, v)} disabled={u.id === me?.id}>
                    <SelectTrigger className="rounded-sm h-8 w-[110px]" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">staff</SelectItem>
                      <SelectItem value="admin">admin</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-6 py-3">
                  {u.role === "admin" ? (
                    <span className="inline-flex items-center gap-1 text-xs font-mono uppercase text-[#0F62FE]">
                      <Coin size={12} weight="fill" /> Unlimited
                    </span>
                  ) : (
                    <div className="inline-flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 font-mono text-sm">
                        <Coin size={14} weight="duotone" className="text-[#F1C21B]" />
                        <span className={`${(u.credits ?? 0) === 0 ? "text-[#DA1E28]" : (u.credits ?? 0) <= 1 ? "text-[#F1C21B]" : "text-foreground"}`}>
                          {u.credits ?? 0}
                        </span>
                      </span>
                      {isAdmin && (
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-[#0F62FE]"
                                onClick={() => { setGrantOpen(u); setGrantAmount(""); }}
                                title={`Grant credits to ${u.email}`}
                                data-testid={`grant-credits-${u.id}`}>
                          <Plus size={12} weight="bold" />
                        </Button>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-6 py-3">
                  {u.disabled ? (
                    <span className="text-xs font-mono uppercase text-[#DA1E28]">Disabled</span>
                  ) : (
                    <span className="text-xs font-mono uppercase text-[#24A148] inline-flex items-center gap-1">
                      <ShieldCheck size={12} weight="fill" /> Active
                    </span>
                  )}
                </td>
                <td className="px-6 py-3 font-mono text-xs text-muted-foreground">{fmtMY(u.created_at)}</td>
                <td className="px-6 py-3 text-right space-x-1">
                  {u.id !== me?.id && (
                    <>
                      <Button size="sm" variant="outline" className="rounded-sm h-8"
                              onClick={() => toggleDisabled(u)} data-testid={`toggle-${u.id}`}>
                        {u.disabled
                          ? <><ArrowClockwise size={14} weight="bold" className="mr-1.5" />Enable</>
                          : <><Prohibit size={14} weight="bold" className="mr-1.5" />Disable</>}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-[#DA1E28] h-8"
                              onClick={() => remove(u)} data-testid={`delete-${u.id}`}>
                        <Trash size={14} weight="bold" />
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Grant Credits dialog */}
      <Dialog open={!!grantOpen} onOpenChange={o => !o && setGrantOpen(null)}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle>Grant credits</DialogTitle>
          </DialogHeader>
          {grantOpen && (
            <div className="space-y-4">
              <div className="text-sm">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Recipient</div>
                <div className="mt-1">{grantOpen.name || "—"} · <span className="text-muted-foreground">{grantOpen.email}</span></div>
                <div className="mt-1 font-mono text-xs">Current balance: <b>{grantOpen.credits ?? 0}</b> credit(s)</div>
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Credits to grant</Label>
                <Input type="number" min={1} className="mt-1 rounded-sm"
                       value={grantAmount}
                       onChange={e => setGrantAmount(e.target.value)}
                       placeholder="e.g. 10"
                       data-testid="grant-credits-amount" />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  One credit is consumed each time this user creates a new tax/secretary file or renews an expiring client.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setGrantOpen(null)}>Cancel</Button>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]"
                    disabled={granting || !grantAmount}
                    onClick={grantCredits}
                    data-testid="grant-credits-confirm">
              {granting ? "Granting…" : "Grant credits"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children, className = "" }) =>
  <th className={`px-6 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-normal ${className}`}>{children}</th>;
